import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../../context/AuthContext';
import courseService from '../../../../services/courseService';
import type { ICourse } from '../../../../types/course';
import Loader from '../../../../components/common/Loader';
import { showToast } from '../../../../utils/showToast';
import { Edit, Trash2, Eye, Plus, Power, PowerOff, Search, BookCopy } from 'lucide-react';
import { Link, useNavigate } from 'react-router-dom';

const ManageCourses: React.FC = () => {
    const { user, isLoading: isAuthLoading } = useAuth();
    const [allCourses, setAllCourses] = useState<ICourse[]>([]);
    const [filteredCourses, setFilteredCourses] = useState<ICourse[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isProcessing, setIsProcessing] = useState<string | null>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const navigate = useNavigate();

    const fetchCourses = async () => {
        setIsLoading(true);
        try {
            const data = await courseService.getAllCoursesForAdmin();
            setAllCourses(data);
            setFilteredCourses(data);
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (!isAuthLoading && user) {
            fetchCourses();
        }
    }, [isAuthLoading, user]);
    
    useEffect(() => {
        const lowercasedFilter = searchTerm.toLowerCase();
        const filteredData = allCourses.filter(course =>
            course.title.toLowerCase().includes(lowercasedFilter)
        );
        setFilteredCourses(filteredData);
    }, [searchTerm, allCourses]);

    const handlePublishToggle = async (courseId: string, courseTitle: string) => {
        const course = allCourses.find(c => c._id === courseId);
        if (!course) return;
        
        const action = course.published ? 'unpublish' : 'publish';
        if (!window.confirm(`Are you sure you want to ${action} "${courseTitle}"?`)) return;

        setIsProcessing(courseId);
        try {
            const { course: updatedCourse } = await courseService.toggleCoursePublication(courseId);
            showToast(`Course "${updatedCourse.title}" has been ${updatedCourse.published ? 'published' : 'unpublished'}.`, { type: 'success' });
            setAllCourses(prev => prev.map(c => c._id === courseId ? updatedCourse : c));
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsProcessing(null);
        }
    };

    const handleDeleteCourse = async (courseId: string, courseTitle: string) => {
        if (!window.confirm(`Are you sure you want to DELETE "${courseTitle}"? This cannot be undone.`)) return;
        setIsProcessing(courseId);
        try {
            const message = await courseService.deleteCourse(courseId);
            showToast(message, { type: 'success' });
            setAllCourses(prev => prev.filter(c => c._id !== courseId));
        } catch (err: any) {
            showToast(err.message, { type: 'error' });
        } finally {
            setIsProcessing(null);
        }
    };
    
    const handleManageCourse = (courseId: string) => {
        const path = user?.isAdmin ? `/dashboard/admin/courses/${courseId}` : `/dashboard/mentor/courses/${courseId}`;
        navigate(path);
    };

    const handleCreateNewCourse = () => {
        const path = user?.isAdmin ? '/dashboard/admin/manage-courses/upload' : '/dashboard/mentor/manage-courses/upload';
        navigate(path);
    };

    if (isAuthLoading || isLoading) {
        return <Loader />;
    }

    const getStatusColor = (published: boolean) => published ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800';

    const renderActionButtons = (course: ICourse) => (
        <div className="flex justify-end items-center gap-1">
             <Link to={`/courses/slug/${course.slug}`} target="_blank" className="p-2 text-gray-500 hover:bg-gray-100 rounded-full" title="View Public Page">
                <Eye className="h-5 w-5" />
            </Link>
            <button onClick={() => handlePublishToggle(course._id, course.title)} disabled={isProcessing === course._id} className="p-2 text-indigo-600 hover:bg-indigo-100 rounded-full" title={course.published ? 'Unpublish' : 'Publish'}>
                {course.published ? <PowerOff className="h-5 w-5" /> : <Power className="h-5 w-5" />}
            </button>
            <button onClick={() => handleManageCourse(course._id)} disabled={isProcessing === course._id} className="p-2 text-blue-600 hover:bg-blue-100 rounded-full" title="Manage Course">
                <Edit className="h-5 w-5" />
            </button>
            <button onClick={() => handleDeleteCourse(course._id, course.title)} disabled={isProcessing === course._id} className="p-2 text-red-600 hover:bg-red-100 rounded-full" title="Delete Course">
                <Trash2 className="h-5 w-5" />
            </button>
        </div>
    );

    return (
        <div className="space-y-6">
            <header className="bg-white p-6 rounded-2xl shadow-sm border border-gray-200">
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                    <div>
                        <h1 className="text-3xl font-bold text-gray-800">Course Management</h1>
                        <p className="text-gray-500 mt-1">Create, publish, and manage all courses.</p>
                    </div>
                    <div className="flex flex-col sm:flex-row w-full md:w-auto gap-2">
                        <div className="relative w-full sm:w-72">
                            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search by course title..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-11 pr-4 py-2.5 border border-gray-300 rounded-full bg-gray-50 focus:ring-2 focus:ring-blue-500 transition"
                            />
                        </div>
                        <button
                            onClick={handleCreateNewCourse}
                            className="w-full sm:w-auto bg-blue-600 text-white px-4 py-2.5 rounded-full text-sm font-medium hover:bg-blue-700 flex items-center justify-center gap-2"
                        >
                            <Plus className="w-4 h-4" />
                            Create Course
                        </button>
                    </div>
                </div>
            </header>

            {filteredCourses.length === 0 ? (
                <div className="text-center py-16 bg-white rounded-2xl shadow-sm border">
                    <BookCopy className="mx-auto h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-lg font-medium text-gray-900">No Courses Found</h3>
                    <p className="mt-1 text-sm text-gray-500">
                        {searchTerm ? 'No courses match your search.' : 'Click "Create Course" to get started.'}
                    </p>
                </div>
            ) : (
                <>
                    <div className="hidden md:block bg-white rounded-2xl shadow-sm border overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Title</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Status</th>
                                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider">Batches</th>
                                    <th className="px-6 py-4 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredCourses.map(course => (
                                    <tr key={course._id}>
                                        <td className="px-6 py-4 font-medium text-gray-900 whitespace-nowrap">{course.title}</td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(course.published)}`}>
                                                {course.published ? 'Published' : 'Draft'}
                                            </span>
                                        </td>
                                        <td className="px-6 py-4 text-sm text-gray-600 whitespace-nowrap">{course.batchCount || 0}</td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {renderActionButtons(course)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="md:hidden space-y-4">
                        {filteredCourses.map(course => (
                            <div key={course._id} className="bg-white p-4 rounded-2xl shadow-sm border space-y-4">
                                <div>
                                    <p className="font-bold text-gray-900">{course.title}</p>
                                </div>
                                <div className="flex items-center justify-between text-sm border-t pt-4">
                                    <div>
                                        <p className="text-gray-600 font-medium mb-1">Status</p>
                                        <span className={`px-2 py-1 inline-flex text-xs font-semibold rounded-full ${getStatusColor(course.published)}`}>
                                            {course.published ? 'Published' : 'Draft'}
                                        </span>
                                    </div>
                                    <div className="text-right">
                                        <p className="text-gray-600 font-medium mb-1">Batches</p>
                                        <span className="font-bold text-gray-800">{course.batchCount || 0}</span>
                                    </div>
                                </div>
                                <div className="border-t pt-2">
                                    {renderActionButtons(course)}
                                </div>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    );
};

export default ManageCourses;